import { Typography } from "@suraasa/placebo-ui"
import { UserOrganizations } from "api/resources/growthspace/types"
import { context } from "global/Context/context"
import { useContext } from "react"
import { useEnrollments } from "utils/hooks/useEnrollments"

import FrequentlyAskedQuestion, {
  FAQType,
} from "@/components/FrequentlyAskedQuestions"

const faqsWhenCpatCompleted: FAQType[] = [
  {
    question: "How do I read my report?",
    answer:
      "Your report provides a detailed breakdown of your strengths and areas for improvement. It highlights key competencies, with recommendations for growth. Use it to identify where you can improve and implement the actionable steps provided.",
  },
  {
    question: "Can I share my report with others?",
    answer:
      "Your report is completely confidential and can only be accessed by you. However, you’re free to discuss your findings with colleagues or mentors for further guidance.",
  },
  {
    question: "How can I improve the areas highlighted in my report?",
    answer:
      "The report includes actionable steps tailored to your performance. You can follow these steps, access additional learning resources, or seek professional development opportunities to strengthen those areas.",
  },
  {
    question: "Can I retake the assessment?",
    answer:
      "Yes! We encourage you to retake the assessment in 6 months to track your progress and see how much you've improved. The report will offer a new set of insights to guide your ongoing development.",
  },
  {
    question: "How often should I take the assessment?",
    answer:
      "To effectively track your progress and continue growing, we recommend retaking the assessment every 12 months. This allows you to measure improvement and adjust your teaching methods accordingly.",
  },
  {
    question: "What if I don't agree with my results?",
    answer:
      "The assessment is designed to provide an objective measure of your pedagogical skills. If you have concerns or questions about your results, you can reach out to our support team for clarification on care@suraasa.com.",
  },
]

const faqsBeforeCompletion: FAQType[] = [
  {
    question: "Is it an online assessment? What is the question pattern?",
    answer:
      "Yes, it is an online assessment. You can attempt it on your smartphone/tablet/laptop/PC. The pattern of the assessment will be 50 Multiple Choice Questions (MCQs) on pedagogy applications.",
  },
  {
    question:
      "How do I prepare for the assessment? Are there any sample question papers for the assessment?",
    answer:
      "The purpose of the assessment is to share your skill analysis with you based on your existing pedagogical knowledge and skills. No prior preparation is required or recommended. This enables you to get unbiased and accurate expert recommendations in your report. There are no sample papers for the assessment. We recommend that you come without any extra preparation for the assessment, in order to get a fair and unbiased report of your teaching potential.",
  },
  {
    question: "Can I pause the assessment midway?",
    answer:
      "To maintain the integrity of your results, the assessment is meant to be completed in one go. Please ensure you have a quiet, uninterrupted hour before you begin.",
  },
  {
    question: "Can I take the assessment again?",
    answer:
      "Yes, we recommend reattempting the assessment after 12 months to track your progress and identify any improvements you've made since your last attempt.",
  },
]

export const CpatFaqs = ({
  assessmentCompleted,
}: {
  assessmentCompleted: boolean
}) => {
  const { currentGrowthspace } = useContext(context)
  const {
    cpat: { throughGSG },
  } = useEnrollments()
  const faqs = getCpatFaqs({
    cpatCompleted: assessmentCompleted,
    currentGrowthspace: currentGrowthspace,
    throughGSG: throughGSG,
  })
  return (
    <div className="flex flex-col">
      <div className="pb-3">
        <Typography variant="title2">Frequently Asked Questions</Typography>
      </div>
      <FrequentlyAskedQuestion FAQs={faqs} />
    </div>
  )
}

const getCpatFaqs = ({
  currentGrowthspace,
  cpatCompleted,
  throughGSG,
}: {
  currentGrowthspace?: UserOrganizations | null
  cpatCompleted: boolean
  throughGSG: boolean
}): FAQType[] => {
  const hideReportRelatedFAQs = throughGSG || currentGrowthspace?.organization
  const reportRelatedFAQs = [
    {
      question:
        "What is the marking/grading scheme for the assessment? Is there negative marking for a wrong answer?",
      answer:
        "You will be answering questions from 6 pedagogy topics with different weightage. There will be no negative marking. In the final report, you will get a band score based on your performance.",
    },
    {
      question: "What will I get after completing the assessment?",
      answer:
        "You'll receive a personalised report outlining your strengths, areas for improvement, and actionable suggestions for professional development.",
    },
    {
      question: "Will you share my assessment report with my school?",
      answer:
        "Your assessment report is 100% confidential and only you will be able to access it. Schools will receive a separate, anonymised report that includes only the overall scores of all participating teachers, without revealing any individual identities.",
    },
  ]

  return cpatCompleted
    ? faqsWhenCpatCompleted
    : [
        ...faqsBeforeCompletion,
        ...(hideReportRelatedFAQs ? [] : reportRelatedFAQs),
      ]
}
